// Core Messaging Hook
// Following existing patterns from useProfileEditor and useActivityFeed

import { useState, useEffect, useCallback } from 'react';
import { 
  Conversation, 
  Message, 
  Attachment,
  UseMessagingReturn 
} from '../../types/messaging';
import { messagingService } from '../../services/Messaging/messagingService';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';

export const useMessaging = (userId: string): UseMessagingReturn => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const { tier } = useSubscriptionContext();
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await messagingService.getConversations(userId);
      setConversations(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch conversations";
      setError(errorMessage);
      console.error("Error fetching conversations:", err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const selectConversation = useCallback((conversation: Conversation | null) => {
    setCurrentConversation(conversation);
  }, []);

  const sendMessage = useCallback(
    async (content: string, attachments?: Attachment[]) => {
      if (!currentConversation || !userId) return;

      try {
        const recipientId = currentConversation.participants.find(
          (p) => p.id !== userId
        )?.id || "";

        const message = await messagingService.sendMessage({
          conversationId: currentConversation.id,
          senderId: userId,
          recipientId,
          content,
          messageType: 'text',
          attachments,
          isRead: false,
          isDelivered: true,
          isEdited: false,
        });

        // Update conversation with new message
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === currentConversation.id
              ? { ...conv, lastMessage: message, updatedAt: new Date() }
              : conv
          )
        );

        // Update current conversation
        setCurrentConversation(prev => 
          prev ? { ...prev, lastMessage: message, updatedAt: new Date() } : null
        );
      } catch (err) {
        // DELIBERATELY NOT setError. This hook's `error` means "the messaging
        // page could not load", and Messaging.tsx renders it as a full-page
        // replacement — so a single rejected send wiped out the conversation
        // list and the thread and left "Error loading messages: You cannot
        // message this user" with no way back short of a reload.
        //
        // A failed send belongs to the conversation it was sent from. It is
        // rethrown, and ChatInterface reports it in place.
        console.error("Error sending message:", err);
        throw err;
      }
    },
    [currentConversation, userId]
  );

  const refreshConversations = useCallback(async () => {
    await fetchConversations();
  }, [fetchConversations]);

  const markMessageAsRead = useCallback(async (messageId: string) => {
    try {
      await messagingService.markAsRead(messageId);
      
      // Update conversation unread count
      setConversations(prev => 
        prev.map(conv => {
          if (conv.lastMessage?.id === messageId) {
            return { ...conv, unreadCount: Math.max(0, conv.unreadCount - 1) };
          }
          return conv;
        })
      );
    } catch (err) {
      console.error("Error marking message as read:", err);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (userId) {
      fetchConversations();
    }
    // Keyed on tier as well: every conversation carries a server-computed
    // canSendMessages, so a plan change has to re-read them or a freshly
    // upgraded member keeps seeing locked composers.
  }, [userId, tier, fetchConversations]);

  return {
    conversations,
    currentConversation,
    isLoading,
    error,
    selectConversation,
    sendMessage,
    refreshConversations,
    markMessageAsRead,
    clearError
  };
};
